import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { SeasonsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";
import { useToast } from "@/components/feedback";

export const useSeasonDelete = () => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: async (seasonId: number) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(SeasonsApi, accessToken);
      return service.apiSeasonsIdDelete({ id: seasonId });
    },
    onSuccess: async (_data, seasonId) => {
      queryClient.removeQueries({ queryKey: [QueryKeys.Seasons, seasonId] });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Seasons] });
      toast({
        title: "Season deleted",
        description: "Season has been deleted successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to delete season. Seasons with associated classes cannot be deleted.",
      );
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending };
};
